// service/WithdrawalService.js
import axios from "axios";
import Cookies from "js-cookie";
import { API_URL } from "helper/Constants";

class WithdrawalService {
  constructor(baseURL) {
    this.api = axios.create({
      baseURL: baseURL ? baseURL : API_URL,
    });
    this.api.interceptors.request.use(
      async (config) => {
        const token = Cookies.get("token");
        if (token) config.headers["Authorization"] = `Bearer ${token}`;
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Try refresh on 401 (same behavior as your DepositService)
    this.api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        if (
          error.response &&
          error.response.status === 401 &&
          error.response?.data?.message !== "Invalid refresh token"
        ) {
          try {
            const newToken = await this.refreshToken();
            if (newToken) {
              originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
              return axios(originalRequest);
            }
          } catch (refreshError) {
            window?.location?.reload();
            return Promise.reject(refreshError);
          }
        }
        return Promise.reject(error);
      }
    );
  }

  async refreshToken() {
    try {
      const response = await this.api.post(
        "/auth/refresh-token",
        {},
        { withCredentials: true }
      );
      const newAccessToken = response.data.accessToken;
      Cookies.set("token", newAccessToken, { secure: true, sameSite: "None" });
      return newAccessToken;
    } catch (error) {
      Cookies.remove("user", { secure: true, sameSite: "None" });
      Cookies.remove("token", { secure: true, sameSite: "None" });
      Cookies.remove("refreshToken", { secure: true, sameSite: "None" });
      window?.location?.reload();
      return null;
    }
  }

  async fetchUserWithdrawals({ status, page_number = 1, items_per_page = 50 } = {}) {
    const token = Cookies.get("token");
    if (!token) throw new Error("User is not authenticated. Token is missing.");

    const params = { page_number, items_per_page };
    if (status) params.status = status;

    const res = await this.api.get("/api/withdrawals/user/my-withdrawals", {
      params,
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!res.data?.success) {
      throw new Error(res.data?.message || "Failed to fetch user withdrawals.");
    }
    return res.data; // { success, data, pagination }
  }

  // POST /api/withdrawals  (no screenshot)
  async createWithdrawal({ paymentMethod, amount, accountDetails, username }) {
    const token = Cookies.get("token");
    if (!token) throw new Error("User is not authenticated. Token is missing.");

    if (!paymentMethod || !amount || !accountDetails || !username) {
      throw new Error("Username, payment method, amount, and account details are required");
    }

    const payload = { paymentMethod, amount, accountDetails, username };

    const res = await this.api.post("/api/withdrawals", payload, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    if (!res.data?.success) {
      throw new Error(res.data?.message || "Failed to create withdrawal.");
    }
    return res.data; // { success, data }
  }

  async cancelWithdrawal(id) {
    const token = Cookies.get("token");
    if (!token) throw new Error("User is not authenticated. Token is missing.");
    if (!id) throw new Error("Withdrawal ID is required");

    const res = await this.api.put(`/api/withdrawals/${id}/cancel`, {}, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!res.data?.success) {
      throw new Error(res.data?.message || "Failed to cancel withdrawal.");
    }
    return res.data;
  }


  async fetchWithdrawals({ status, user, paymentMethod, startDate, endDate, page_number = 1, items_per_page = 50 } = {}) {
    const token = Cookies.get("token");
    if (!token) throw new Error("User is not authenticated. Token is missing.");

    const params = { page_number, items_per_page };
    if (status) params.status = status;
    if (user) params.user = user;
    if (paymentMethod) params.paymentMethod = paymentMethod;
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;

    const res = await this.api.get("/api/withdrawals", {
      params,
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!res.data?.success) {
      throw new Error(res.data?.message || "Failed to fetch withdrawals.");
    }
    return res.data;
  }
    async allowWithdrawal(id) {
    const res = await this.api.patch(`/api/withdrawals/${id}/allow`);
    if (!res.data?.success) throw new Error(res.data?.message || "Failed to allow withdrawal");
    return res.data;
  }

  async approveWithdrawal(id, { adminNotes = "" } = {}, screenshotFile = null) {
    const token = Cookies.get("token");
    if (!token) throw new Error("User is not authenticated. Token is missing.");
    if (!id) throw new Error("Withdrawal ID is required");

    let body;
    let headers = { Authorization: `Bearer ${token}` };

    if (screenshotFile) {
      body = new FormData();
      body.append("adminNotes", adminNotes);
      body.append("screenshot", screenshotFile);
      headers["Content-Type"] = "multipart/form-data";
    } else {
      body = { adminNotes };
      headers["Content-Type"] = "application/json";
    }

    const res = await this.api.patch(`/api/withdrawals/${id}/approve`, body, { headers });

    if (!res.data?.success) {
      throw new Error(res.data?.message || "Failed to approve withdrawal.");
    }
    return res.data;
  }

  async rejectWithdrawal(id, rejectionReason, adminNotes = "") {
    const token = Cookies.get("token");
    if (!token) throw new Error("User is not authenticated. Token is missing.");
    if (!id) throw new Error("Withdrawal ID is required");
    if (!rejectionReason) throw new Error("Rejection reason is required");

    const res = await this.api.patch(
      `/api/withdrawals/${id}/reject`,
      { rejectionReason, adminNotes },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );

    if (!res.data?.success) {
      throw new Error(res.data?.message || "Failed to reject withdrawal.");
    }
    return res.data;
  }
}

export default WithdrawalService;
